// AppContent.js
import React, { useState } from 'react';
import { useSwipeable } from 'react-swipeable';
import { motion, AnimatePresence } from 'framer-motion';
import { useLocation, useNavigate } from 'react-router-dom';
import NavBar from './NavBar'; // Import the Navbar component 
import { FaWindows, FaApple, FaAndroid, FaDiscord } from 'react-icons/fa'; // Importing icons
import './AppContent.css'; // Create a separate CSS file if needed
import IrisEye from './IrisEye';
import PrivacyPolicyModal from './PrivacyPolicyModal';
import emailjs from 'emailjs-com';
import { useRef } from 'react';


import logoFull from './img/logo_full_white_noIris.svg';
import screenshot_01 from './img/screenshots/screenshot_01.webp';
import screenshot_02 from './img/screenshots/screenshot_02.webp';
import screenshot_03 from './img/screenshots/screenshot_03.webp';
import screenshot_04 from './img/screenshots/screenshot_04.webp';
import screenshot_05 from './img/screenshots/screenshot_05.webp';
import screenshot_06 from './img/screenshots/screenshot_06.webp';
import touchdesignerIcon from './img/touchdesigner_logo.svg';
import ndiIcon from './img/ndi_logo.svg';
import HeroParallax from './HeroParallax';

/* Animation Variants */
const fadeInVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.8 }
  }
};

const galleryContainerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2,
    },
  },
};

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15,
    }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, ease: "easeOut" }
  }
};

const galleryItemVariants = {
  hidden: { opacity: 0, scale: 0.8 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: {
      duration: 0.5,
      ease: "easeOut",
    },
  },

  hover: {
    scale: 1.05,
    boxShadow: "0px 4px 15px rgba(0, 0, 0, 0.2)",
    transition: {
      duration: 0.3,
      ease: "easeOut",
    },
  },
};

function AppContent() {

  const handleSubmit = (e) => {
    e.preventDefault(); // Prevent default form submission

    emailjs.sendForm(
      process.env.REACT_APP_EMAILJS_SERVICE_ID,
      process.env.REACT_APP_EMAILJS_TEMPLATE_ID,
      form.current,
      process.env.REACT_APP_EMAILJS_USER_ID
    )
      .then((result) => {
        console.log(result.text);
        alert('Your message has been sent successfully!');
        // Optionally, reset the form
        form.current.reset();
      }, (error) => {
        console.log(error.text);
        alert('An error occurred while sending your message. Please try again later.');
      });
  };

  const form = useRef();
  const [selectedImage, setSelectedImage] = useState(null);
  const images = [
    screenshot_01,
    screenshot_02,
    screenshot_03,
    screenshot_04,
    screenshot_05,
    screenshot_06
  ];
  const location = useLocation();
  const navigate = useNavigate();

  const isPrivacyPolicyOpen = location.pathname === '/privacypolicy';

  const handleCloseModal = () => {
    navigate('/'); // Navigate back to home when closing
  };

  const handleOpenModal = () => {
    navigate('/privacypolicy');
  };

  const handleNext = () => {
    const currentIndex = images.indexOf(selectedImage);
    const nextIndex = (currentIndex + 1) % images.length;
    setSelectedImage(images[nextIndex]);
  };

  const handlePrev = () => {
    const currentIndex = images.indexOf(selectedImage);
    const prevIndex = (currentIndex - 1 + images.length) % images.length;
    setSelectedImage(images[prevIndex]);
  };

  /* Swipe Handlers */
  const handlers = useSwipeable({
    onSwipedLeft: () => handleNext(),
    onSwipedRight: () => handlePrev(),
    preventDefaultTouchmoveEvent: true,
    trackMouse: true
  });

  return (
    <>
      {/* Navbar */}
      <NavBar />
      {/* Hero Section */}
      <section className="hero" style={{ position: 'relative', overflow: 'hidden' }}>
        <HeroParallax />
        <motion.section
          className="hero-content"
          style={{ position: 'relative', zIndex: 2 }}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
          variants={fadeInVariants}
        >
          <div className="logo-wrapper">
            <img
              src={logoFull}
              alt="SeeOSC Full Logo"
              className="full-logo"
            />
            <div className="iris-overlay">
              <IrisEye />
            </div>
          </div>

          <p>
            A powerful app for interactive &amp; experimental digital media artists.
            Send sensor data, share camera feeds, and control phone hardware utilising OSC, WebRTC/TouchDesigner and NDI®.
          </p>

          <div className="download-links">
            <button
              className="cta-button"
              onClick={() =>
                document.getElementById('downloads').scrollIntoView({ behavior: 'smooth' })
              }
            >
              Download
            </button>
          </div>
        </motion.section>
      </section>

      {/* About Section */}
      <motion.section
        className="about"
        id="about"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.3 }}
        variants={fadeInVariants}
      >
        <h2>About SeeOSC</h2>
        <p>
          SeeOSC was built from the ground up for digital artists who want to blur the
          lines between physical and virtual worlds. By leveraging OSC,
          you can send sensor data from your phone—accelerometer, gyroscope, touch
          coordinates etc, all while sending and receiving NDI or WebRTC video streams in real-time.
        </p>
        <p>
          Create live visuals projected on-stage, all controlled by the tilt of a
          performer’s phone, prototype and develop interfaces rendered by a remote system, or create
          immersive interactive experiences driven by the camera and sensors of a mobile device.
          The possibilities are endless.
        </p>
      </motion.section>

      {/* Features Section */}
      <motion.section
        className="features"
        id="features"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.1 }}
        variants={fadeInVariants}
      >
        <h2>Features</h2>
        <motion.div
          className="feature-list"
          variants={containerVariants}  // parents for stagger
          initial="hidden"
          animate="visible"
        >
          <motion.div
            className="feature"
            variants={itemVariants}         // child animation
            whileHover={{ scale: 1.03 }}   // scale up slightly on hover
            whileTap={{ scale: 0.97 }}     // squish a bit on click
          >
            <h3>Sensor Data via OSC</h3>
            <p>Send a plethora sensor data from your phone for real-time visuals and audio manipulation.</p>
          </motion.div>
          <motion.div
            className="feature"
            variants={itemVariants}
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
          >
            <h3>Flexible Video Streaming</h3>
            <p>Send your camera feed and receive video streams. Choose between NDI for high-fidelity local network streaming or our WebRTC/TouchDesigner implementation - create powerful visuals offloaded from the phone's hardware</p>
          </motion.div>
          <motion.div
            className="feature"
            variants={itemVariants}
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
          >
            <h3>NDI Video Integration</h3>
            <p>Utilize the industry-standard NDI protocol for sending and receiving high-quality, low-latency video and audio over your local network. <a href="https://ndi.video/" target="_blank" rel="noopener noreferrer">Learn more about NDI</a>.</p> {/* Added link */}
          </motion.div>
          <motion.div
            className="feature"
            variants={itemVariants}
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
          >
            <h3>Custom Control Surface</h3>
            <p>Create a custom control surface to send OSC, MIDI or DMX, while also sending sensor data</p>
          </motion.div>
          <motion.div
            className="feature"
            variants={itemVariants}
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
          >
            <h3>Phone Hardware Control</h3>
            <p>Receive OSC commands to change screen brightness, vibration, and other device settings.</p>
          </motion.div>
          <motion.div
            className="feature"
            variants={itemVariants}
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
          >
            <h3>Web-Based Config</h3>
            <p>Access and configure app settings from any browser on the local network—no more tiny menus.</p>
          </motion.div>
          <motion.div
            className="feature"
            variants={itemVariants}
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
          >
            <h3>Handy Extras</h3>
            <p>Send dummy sensor data to free your hands and transmit device info for convenient programming.</p>
          </motion.div>
          <motion.div
            className="feature"
            variants={itemVariants}
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
          >
            <h3>Display Options</h3>
            <p>Choose to either display an incoming video stream, a local image/video or a blank screen when sending sensor data.</p>
          </motion.div>
          <motion.div
            className="feature"
            variants={itemVariants}
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
          >
            <h3>Industry Standard Protocols</h3>
            <p>Utilises OSC for sharing sensor data and Spout for incoming and outgoing video streams.</p>
          </motion.div>
          <motion.div
            className="feature"
            variants={itemVariants}
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
          >
            <h3>Easy to Use</h3>
            <p>Streamlined user-interface with helpful tooltips for every setting.</p>
          </motion.div>
        </motion.div>
      </motion.section>

      {/* Demo / Video Section */}
      <motion.section
        className="demo"
        id="demo"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.3 }}
        variants={fadeInVariants}
      >
        <h2>In Action</h2>
        <p>See how SeeOSC can be used in a live performance setting.</p>
        <div className="video-container">
          <iframe
            width="560"
            height="315"
            src="https://www.youtube.com/embed/QkU6bZ4yj8w?si=V8tPdnZBimQDvRG4&amp;controls=0"
            title="SeeOSC"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            referrerPolicy="strict-origin-when-cross-origin"
            allowFullScreen
          />
        </div>
      </motion.section>

      {/* Gallery Section */}
      <motion.section
        className="gallery"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.3 }}
        variants={fadeInVariants}
      >
        <h2>Screenshots</h2>
        <motion.div
          className="gallery-images"
          variants={galleryContainerVariants}
          initial="hidden"
          animate="visible"
        >
          {images.map((src, index) => (
            <motion.div
              key={index}
              className="gallery-item"
              variants={galleryItemVariants}
              whileHover="hover"
              onClick={() => setSelectedImage(src)}
              role="button"
              tabIndex={0}
              aria-label={`View screenshot ${index + 1}`}
              onKeyPress={(e) => { if (e.key === 'Enter') setSelectedImage(src); }}
            >
              <motion.img src={src} alt={`App Screenshot ${index + 1}`} loading="lazy" />
            </motion.div>
          ))}
        </motion.div>

        {/* Lightbox with Navigation and Swipe Controls */}
        <AnimatePresence>
          {selectedImage && (
            <motion.div
              className="lightbox"
              {...handlers} // Attach swipe handlers here
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setSelectedImage(null)}
              role="dialog"
              aria-modal="true"
            >
              <motion.img
                src={selectedImage}
                alt="Enlarged Screenshot"
                initial={{ scale: 0.8 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0.8 }}
                transition={{ duration: 0.3 }}
                onClick={(e) => e.stopPropagation()}
              />
              <button
                className="prev-button"
                onClick={(e) => {
                  e.stopPropagation();
                  handlePrev();
                }}
                aria-label="Previous Image"
              >
                ‹
              </button>
              <button
                className="next-button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleNext();
                }}
                aria-label="Next Image"
              >
                ›
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.section>

      {/* Downloads Section */}
      <motion.section
        className="downloads"
        id="downloads"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.3 }}
        variants={fadeInVariants}
      >
        <h2>Get the App and TouchDesigner TOX</h2>
        <div className="download-section">
          {/* Desktop Companion App 
          <div className="desktop-download">
            <h3>Get the Companion App</h3>
            <div className="download-buttons">
              <a
                className="download-button"
                href="/downloads/SeeOSC_win.zip" // Update to .exe or separate files as needed
                download
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Download for Windows"
              >
                <FaWindows className="download-icon" /> Windows
              </a>
              <a
                className="download-button disabled"
                href="/downloads/SeeOSC_mac.zip" // Update to .dmg for Mac if separate
                download
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Download for Mac (coming soon)"
                onClick={(e) => e.preventDefault()} // Prevent download
              >
                <FaApple className="download-icon" /> Mac (coming soon)
              </a>
            </div>
          </div>
*/}
          {/* Mobile App */}
          <div className="mobile-download">
            <h3>Get the Mobile App</h3>
            <div className="download-buttons">
              {/*}
              <a
                className="download-button disabled"
                href="#0" // Replace with actual App Store link when available
                aria-label="Download for Android (coming soon)"
                onClick={(e) => e.preventDefault()} // Prevent click
              >
                <FaAndroid className="download-icon" /> Android (coming soon)
              </a>
              */}
              <a
                className="download-button"
                href="https://apps.apple.com/au/app/seeosc/id6740987202"
                aria-label="Download for iOS"
              >
                <FaApple className="download-icon" /> iOS
              </a>
            </div>
          </div>
          <div className="touchdesigner-download">
            <h3>Get the TouchDesigner TOX</h3>
            <div className="download-buttons">
              <a
                className="download-button"
                href="/downloads/SeeOSC_TouchIntegration.zip"
                download
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Download TouchDesigner TOX"
              >
                <img
                  src={touchdesignerIcon}
                  alt="TouchDesigner Icon"
                  className="download-icon"
                />
                TouchDesigner TOX
              </a>
            </div>
          </div>
        </div>
<br /> <br />
        <div className="mobile-download">
                                  <h3>
            Get NDI tools from Vizrt NDI AB
          </h3>
          <div className="download-buttons">
            <a
              className="download-button"
              href="https://ndi.video/tools/"
              aria-label="Get NDI Tools"
            >
              <img
                src={ndiIcon}
                alt="NDI Icon"
                className="download-icon"
              /> Get NDI Tools
            </a>
          </div>
        </div>
      </motion.section>

      <motion.section
        className="contact"
        id="contact"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.3 }}
        variants={fadeInVariants}
      >
        <h2>Get in Touch</h2>
        <div>
          <p>
            For questions, feature requests, any help, or just to say hi - send me a message on Discord:
          </p>
        </div>
        <div className="mobile-download">
          <div className="download-buttons">
            <a
              className="download-button"
              href="https://discord.gg/2sF5VJxRy8"
              aria-label="Join the Discord"
            >
              <FaDiscord className="download-icon" /> Join the Discord
            </a>
          </div>
        </div>
      </motion.section>

      {/* Footer with Privacy Policy Link */}
      <footer>
        © 2025 Jayden Allen |
        <a
          href="/privacypolicy"
          onClick={(e) => {
            e.preventDefault();
            handleOpenModal();
          }}
          style={{ color: '#666', marginLeft: '8px' }}
        >
          Privacy Policy
        </a>
      </footer>

      {/* Privacy Policy Modal */}
      <AnimatePresence>
        {isPrivacyPolicyOpen && (
          <PrivacyPolicyModal handleClose={handleCloseModal} />
        )}
      </AnimatePresence>
    </>
  );
}

export default AppContent;
